import type { TFunction } from "i18next";

/**
 * Catalog copy overrides.
 *
 * Tool name/description live in the database (seeded in English by migration),
 * so first-party tools override them with translated copy keyed by slug.
 * Unknown slugs fall back to whatever the catalog returns.
 */
const TOOL_COPY_KEYS: Record<string, { name: string; short: string; description: string }> = {
    constellation: {
        name: "constellation.toolName",
        short: "constellation.toolShort",
        description: "constellation.toolDescription",
    },
    "ai-insights": {
        name: "aiInsights.toolName",
        short: "aiInsights.toolShort",
        description: "aiInsights.toolDescription",
    },
    scheduler: {
        name: "scheduler.toolName",
        short: "scheduler.toolShort",
        description: "scheduler.toolDescription",
    },
    payments: {
        name: "payments.toolName",
        short: "payments.toolShort",
        description: "payments.toolDescription",
    },
    "alchely-meeting": {
        name: "alchelyMeetingTool.toolName",
        short: "alchelyMeetingTool.toolShort",
        description: "alchelyMeetingTool.toolDescription",
    },
    "genogram-ecomap": {
        name: "systemicMap.toolName",
        short: "systemicMap.toolShort",
        description: "systemicMap.toolDescription",
    },
    meditation: {
        name: "meditation.toolName",
        short: "meditation.toolShort",
        description: "meditation.toolDescription",
    },
    emdr: {
        name: "emdr.toolName",
        short: "emdr.toolShort",
        description: "emdr.toolDescription",
    },
};

/** Enough of a tool to name it: the slug decides, the rest is a fallback. */
export interface ToolLike {
    slug: string;
    name?: string;
    short_description?: string;
    description?: string;
}

export const toolName = (t: TFunction, tool: ToolLike): string =>
    TOOL_COPY_KEYS[tool.slug] ? t(TOOL_COPY_KEYS[tool.slug].name) : tool.name ?? "";

export const toolShortDescription = (t: TFunction, tool: ToolLike): string =>
    TOOL_COPY_KEYS[tool.slug] ? t(TOOL_COPY_KEYS[tool.slug].short) : tool.short_description ?? "";

export const toolDescription = (t: TFunction, tool: ToolLike): string =>
    TOOL_COPY_KEYS[tool.slug] ? t(TOOL_COPY_KEYS[tool.slug].description) : tool.description ?? "";

/**
 * What a module is called where it is **paid for**, when that differs from
 * the tool's own name.
 *
 * Constellation is sold as "Terapia sistémica": the price buys it and
 * Genogram & Ecomap together, and that is the name on the Stripe product, the
 * checkout and the invoice. The tool itself is still Constellation wherever
 * it is opened. Every other module is billed under its own name.
 */
const MODULE_NAME_KEYS: Record<string, string> = {
    constellation: "constellation.moduleName",
};

export const moduleName = (t: TFunction, tool: ToolLike): string =>
    MODULE_NAME_KEYS[tool.slug] ? t(MODULE_NAME_KEYS[tool.slug]) : toolName(t, tool);
